import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { groupBy } from 'lodash'
import DOMPurify from 'dompurify'
import { getFileIcon, replaceExtension } from '../../utils/functions'
import { DownloadModernIcon, ExpandLessIcon, ExpandMoreIcon } from '../../icons'
import { downloadFirmware } from '../../utils/requests/firmwareAPI'
import { convertToSimpleDate } from '../../utils/filters/date'
import './firmware-version-history.scss'

const VersionHistory = (props) => {
  const { t } = useTranslation()
  const { selectedDevice, versionExpanded, toggleExpandVersion } = props
  const [isRunning, setIsRunning] = useState(false)
  const hideSoftwares = selectedDevice?.model?.toLowerCase().includes('hydrim 112')

  const softwareTypeLabels = {
    UI: t('firmware_context.lcd_board_software'),
    Control: t('firmware_context.main_board_software'),
    'UI+Control': t('firmware_context.lcd_main_board_software'),
    Instruction: t('firmware_context.instructions_other_documents'),
  }

  const hasValidNotes = (notes) => {
    return notes && notes.length > 0 && notes !== '<p></p>' && notes !== '<p><br></p>'
  }

  const downloadFile = (file) => {
    setIsRunning(true)

    const body = {
      filename: file.filename,
      reference_id: file.reference_id,
      prefix: selectedDevice.prefix,
      prefix_number: selectedDevice.prefix_number,
      email: props.authedUser.email,
      country: props.authedUser.country,
      type: file?.software_type === 'Instruction' ? 'document' : 'firmware',
    }

    downloadFirmware(file.id, file.filename, file.filepath, body)
      .then(() => {
        setIsRunning(false)
      })
      .catch(() => {
        setIsRunning(false)
      })
  }

  const renderFilesByCategory = (version) => {
    const groupBySoftwareType = groupBy(version.files, 'software_type')

    return (
      <>
        {Object.entries(groupBySoftwareType).map(([software_type, files]) => {
          if (hideSoftwares && (software_type === 'Control' || software_type === 'UI')) {
            return null
          }

          return (
            <div key={software_type} className="firmware-documents">
              <div className="inner-separator" />
              <div className="section-title">{softwareTypeLabels[software_type]}</div>
              <div className="firmware-executables-wrapper">
                {files.map((file) => {
                  return (
                    <>
                      <div className="firmware-executable">
                        <button className="download-document" onClick={() => downloadFile(file)} disabled={isRunning}>
                          <DownloadModernIcon className="download-icon" />
                        </button>
                        {getFileIcon(file.filename)}
                        <div className="file-name">{replaceExtension(file.label || file.filename)}</div>
                      </div>
                    </>
                  )
                })}
              </div>
            </div>
          )
        })}
      </>
    )
  }

  return (
    <div className="firmware-version-history">
      {selectedDevice.versions.map((version, index) => (
        <div key={`version-${index}`} className="version-expand-wrapper">
          <div
            className={`version-expand-header d-flex flex-justify-between flex-align-center ${index === versionExpanded ? 'highlight' : 'border-bottom'}`}
            onClick={() => toggleExpandVersion(index)}
          >
            <div className="title">
              <div>{version.name}</div>
              <div>{`${t('uploaded')}: ${convertToSimpleDate(version.created_at)}`}</div>
            </div>
            <div className="icon">{versionExpanded === index ? <ExpandLessIcon /> : <ExpandMoreIcon />}</div>
          </div>
          {versionExpanded === index && (
            <div className="version-expand-body">
              <div className="versions-notes">
                {hasValidNotes(version.release_notes?.admin_note) && (
                  <div>
                    <p className="section-name">{t('admin_notes')}</p>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(version?.release_notes?.admin_note),
                      }}
                    />
                  </div>
                )}
                {hasValidNotes(version?.release_notes?.admin_note) && hasValidNotes(version?.release_notes?.tech_note) && <div className="separator" />}
                {hasValidNotes(version?.release_notes?.tech_note) && (
                  <div>
                    <p className="section-name">{t('tech_notes')}</p>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(version?.release_notes?.tech_note),
                      }}
                    />
                  </div>
                )}
                {(hasValidNotes(version?.release_notes?.admin_note) || hasValidNotes(version?.release_notes?.tech_note)) &&
                  hasValidNotes(version?.release_notes?.user_note) && <div className="separator" />}
                {hasValidNotes(version?.release_notes?.user_note) && (
                  <div>
                    <p className="section-name">{t('user_notes')}</p>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(version?.release_notes?.user_note),
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="versions-download-list">{renderFilesByCategory(version)}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function mapStateToProps({ authedUser }) {
  return {
    authedUser,
  }
}

export default connect(mapStateToProps)(VersionHistory)
